import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { ToastContainer } from '@/components/ui/ToastContainer'
import type { ToastProps } from '@/components/ui/Toast'

interface ToastContextValue {
    showToast: (toast: Omit<ToastProps, 'id'>) => void
    success: (title: string, message: string, details?: ReactNode, duration?: number) => void
    error: (title: string, message: string, details?: ReactNode, duration?: number) => void
    warning: (title: string, message: string, details?: ReactNode, duration?: number) => void
    info: (title: string, message: string, details?: ReactNode, duration?: number) => void
    removeToast: (id: string) => void
    /** Set to true when on a page with a map component */
    setIsMapPage: (isMap: boolean) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastProps[]>([])
    const [isMapPage, setIsMapPage] = useState(false)

    const showToast = useCallback((toast: Omit<ToastProps, 'id'>) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        setToasts(prev => [...prev, { ...toast, id }])
    }, [])

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id))
    }, [])

    // Convenience methods
    const success = useCallback((title: string, message: string, details?: ReactNode, duration: number = 5000) => {
        showToast({ title, message, variant: 'success', details, duration })
    }, [showToast])

    const error = useCallback((title: string, message: string, details?: ReactNode, duration: number = 7000) => {
        showToast({ title, message, variant: 'error', details, duration })
    }, [showToast])

    const warning = useCallback((title: string, message: string, details?: ReactNode, duration: number = 6000) => {
        showToast({ title, message, variant: 'warning', details, duration })
    }, [showToast])

    const info = useCallback((title: string, message: string, details?: ReactNode, duration: number = 12000) => {
        showToast({ title, message, variant: 'info', details, duration })
    }, [showToast])

    return (
        <ToastContext.Provider value={{ showToast, success, error, warning, info, removeToast, setIsMapPage }}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} isMapPage={isMapPage} />
        </ToastContext.Provider>
    )
}

export function useToast() {
    const context = useContext(ToastContext)
    if (!context) {
        throw new Error('useToast must be used within ToastProvider')
    }
    return context
}

/**
 * Hook to register current page as a map page.
 * Call this at the top of any page component that has a map.
 * Toast notifications will appear at bottom-center of the map.
 */
export function useMapPageToast() {
    const { setIsMapPage } = useToast()

    useEffect(() => {
        setIsMapPage(true)
        return () => setIsMapPage(false)
    }, [setIsMapPage])
}
